import { useRef, useState, type ReactNode } from 'react';
import { Box, Button, HStack, Stack, Text } from '@chakra-ui/react';
import type { Bridge } from '@casa/bridge';
import { DriverPackageError, installDriver, readDriverPackage, type DriverFile, type DriverPackage, type LoadedDriver } from '@casa/driver-host';
import type { DriverManifest, DriverPermissions } from '@casa/driver-sdk';
import { TextFieldRow } from '../forms/form_rows.tsx';
import { SettingsGroup, SettingsRow } from '../settings/settings_ui.tsx';
import { Waiting } from '../ui/asking.tsx';
import { ChromeError } from '../ui/chrome_page.tsx';
import { filesFromFolder, filesFromUrl, filesFromZip } from './driver_sources.ts';
import { BUILT_IN_IDS } from './drivers.ts';

// Installing a driver (proposal §6.3, dashboard-editor spec C-7): from a folder, a zip file or a
// web address; then what it may reach, its version and the SHA-256 of its module, which the user
// accepts before anything is stored. Used in the settings (Drivers) and when adding a connection
// ("Add a driver…", C-2). Not offered where installed drivers do not run (iOS, H-4); the caller
// asks the catalog's profile.

/** What a driver may reach, in words, for the consent screen and the list. */
export function describePermissions(permissions: DriverPermissions) : string[] {
    const places = [
        ...permissions.configHosts.map((key) => `the host named in its setting "${key}"`),
        ...permissions.hosts.map((pattern) => (pattern.startsWith('*.') ? `any host under ${pattern.slice(2)}` : pattern)),
    ];
    if (places.length === 0) {
        return ['Nothing on the network.'];
    }

    const kinds = ['HTTP', ...(permissions.ws ? ['WebSocket'] : []), ...(permissions.tcp ? ['raw TCP'] : [])];

    return [`${kinds.join(', ')} connections to:`, ...places.map((place) => `• ${place}`)];
}

export function Permissions({ permissions }: { permissions: DriverPermissions }) : ReactNode {
    return (
        <Box>
            {describePermissions(permissions).map((line) => (
                <Text key={line}>{line}</Text>
            ))}
        </Box>
    );
}

export function Hash({ sha256 }: { sha256: string }) : ReactNode {
    return (
        <Text color="fg.muted" fontSize="xs" fontFamily="mono" wordBreak="break-all">
            SHA-256 {sha256}
        </Text>
    );
}

function Consent(props: { driverPackage: DriverPackage; replaces: LoadedDriver | undefined; onAccept: () => void; onCancel: () => void }) : ReactNode {
    const { manifest, sha256 } = props.driverPackage;
    const replaced = props.replaces?.ok ? props.replaces.record : undefined;

    return (
        <Box role="group" aria-label="Install driver" borderRadius="xl" borderWidth="1px" borderColor="blue.500" bg="bg.panel" p="4">
            <Stack gap="2">
                <Text fontWeight="semibold" fontSize="lg">
                    Install {manifest.name} {manifest.version}?
                </Text>
                <Text color="fg.muted" fontSize="sm">
                    {manifest.id}
                    {replaced ? ` · replaces version ${replaced.version}, installed ${new Date(replaced.consentedAt).toLocaleDateString()}` : ''}
                </Text>
                {manifest.description ? <Text>{manifest.description}</Text> : null}
                <Text mt="2">This driver may make</Text>
                <Permissions permissions={manifest.permissions} />
                <Text color="fg.muted" fontSize="sm">
                    A driver gets the settings of its connection, passwords and tokens included. It can send them only to the places above; it has no other way
                    to the network.
                </Text>
                <Hash sha256={sha256} />
                <HStack mt="2">
                    <Button colorPalette="blue" onClick={props.onAccept}>
                        Install
                    </Button>
                    <Button variant="outline" onClick={props.onCancel}>
                        Cancel
                    </Button>
                </HStack>
            </Stack>
        </Box>
    );
}

export interface DriverInstallProps {
    shell: Bridge;
    /** The installed drivers, to say which one a package replaces. */
    installed: readonly LoadedDriver[];
    onInstalled: (manifest: DriverManifest) => void;
}

/** Choosing a driver package (a folder, a zip file, a web address), then the consent, then installing it. */
export function DriverInstall({ shell, installed, onInstalled }: DriverInstallProps) : ReactNode {
    const [candidate, setCandidate] = useState<DriverPackage>();
    const [error, setError] = useState<string>();
    const [isReading, setReading] = useState(false);
    const [isAskingAddress, setAskingAddress] = useState(false);
    const folderInput = useRef<HTMLInputElement>(null);
    const zipInput = useRef<HTMLInputElement>(null);

    const read = async (files: () => Promise<DriverFile[]>) : Promise<void> => {
        setError(undefined);
        setCandidate(undefined);
        setReading(true);
        try {
            setCandidate(await readDriverPackage(await files(), BUILT_IN_IDS));
            setAskingAddress(false);
        } catch (caught) {
            const issues = caught instanceof DriverPackageError ? caught.issues.map((issue) => `\n- ${issue}`).join('') : '';
            setError(`${caught instanceof Error ? caught.message : String(caught)}${issues}`);
        } finally {
            setReading(false);
        }
    };
    const accept = async (driverPackage: DriverPackage) : Promise<void> => {
        try {
            await installDriver(shell.storage, driverPackage, new Date());
            setCandidate(undefined);
            onInstalled(driverPackage.manifest);
        } catch (caught) {
            setError(`Installing failed: ${caught instanceof Error ? caught.message : String(caught)}`);
        }
    };

    if (candidate) {
        return (
            <Consent
                driverPackage={candidate}
                replaces={installed.find((driver) => (driver.ok ? driver.record.id : driver.id) === candidate.manifest.id)}
                onAccept={() => void accept(candidate)}
                onCancel={() => setCandidate(undefined)}
            />
        );
    }

    return (
        <Stack gap="3">
            <SettingsGroup title="Install a driver" note="A driver is a folder (or a zip file of it) with its driver.json and its module. You see what it may reach before it is installed.">
                <SettingsRow icon="folder-open" title="From a folder…" opens={false} onClick={() => folderInput.current?.click()} />
                <SettingsRow icon="file-zipper" title="From a zip file…" opens={false} onClick={() => zipInput.current?.click()} />
                <SettingsRow icon="globe" title="From a web address…" opens={false} onClick={() => setAskingAddress(!isAskingAddress)} />
                {isAskingAddress ? (
                    <TextFieldRow
                        label="Address"
                        detail="Of a zip file, or of a driver.json with the module beside it."
                        value={undefined}
                        placeholder="https://…"
                        onChange={(address) => {
                            if (address !== undefined) {
                                void read(() => filesFromUrl(shell, address));
                            }
                        }}
                    />
                ) : null}
            </SettingsGroup>
            <input
                ref={folderInput}
                type="file"
                data-testid="driver-folder"
                multiple
                {...{ webkitdirectory: '' }}
                style={{ display: 'none' }}
                onChange={(event) => {
                    const { files } = event.target;
                    if (files && files.length > 0) {
                        void read(() => filesFromFolder(files));
                    }
                    event.target.value = '';
                }}
            />
            <input
                ref={zipInput}
                type="file"
                data-testid="driver-zip"
                accept=".zip,application/zip"
                style={{ display: 'none' }}
                onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) {
                        void read(async () => filesFromZip(new Uint8Array(await file.arrayBuffer())));
                    }
                    event.target.value = '';
                }}
            />
            {isReading ? <Waiting text="Reading the driver…" /> : null}
            {error ? <ChromeError text={error} /> : null}
        </Stack>
    );
}
